import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel, { UseEmblaCarouselType } from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

type EmblaApi = NonNullable<UseEmblaCarouselType[1]>;

const useEmblaDots = (emblaApi?: EmblaApi) => {
    const [count, setCount] = useState(0);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        if (!emblaApi) {
            return;
        }

        const updateCount = (api: EmblaApi) => setCount(api.scrollSnapList().length);
        const updateActiveIndex = () => setActiveIndex(emblaApi.selectedScrollSnap());

        updateCount(emblaApi);
        setActiveIndex(emblaApi.selectedScrollSnap());

        emblaApi.on('select', updateActiveIndex);
        emblaApi.on('reInit', updateCount);

        return () => {
            emblaApi.off('select', updateActiveIndex);
            emblaApi.off('reInit', updateCount);
        };
    }, [emblaApi]);

    return { count, activeIndex };
};

/** Функция для применения бесшовного перехода между слайдами */
const applyOverlay = (emblaApi: EmblaApi, bgColors: string[]) => {
    const slides = emblaApi.slideNodes();
    const scrollProgress = emblaApi.scrollProgress();
    const snapList = emblaApi.scrollSnapList();
    const slideCount = slides.length;

    slides.forEach((slide: HTMLElement, index: number) => {
        const snapProgress = snapList[index];
        const linearDistance = scrollProgress - snapProgress;

        // При loop учитываем цикличность: берём кратчайший путь по окружности
        const wrappedDistance =
            Math.abs(linearDistance) > 0.5 ? linearDistance - Math.sign(linearDistance) : linearDistance;

        const transitionProgress = Math.min(1, Math.abs(wrappedDistance) * slideCount);

        // wrappedDistance > 0 означает что scrollProgress > snapProgress — мы ушли вперёд от этого слайда
        const isScrollingForward = wrappedDistance > 0;
        const nextIndex = isScrollingForward
            ? (index + 1) % bgColors.length
            : (index - 1 + bgColors.length) % bgColors.length;
        const nextBgColor = bgColors[nextIndex] ?? 'transparent';

        slide.style.setProperty('--overlay-color', nextBgColor);
        slide.style.setProperty('--overlay-opacity', String(transitionProgress));
    });
};

const clearOverlay = (emblaApi: EmblaApi) => {
    emblaApi.slideNodes().forEach((slide: HTMLElement) => {
        slide.style.setProperty('--overlay-opacity', '0');
    });
};

/** Хук для применения бесшовного перехода между слайдами */
const useEmblaGradient = (emblaApi: EmblaApi | undefined, bgColors: string[]) => {
    useEffect(() => {
        if (!emblaApi) {
            return;
        }

        // Сбрасываем overlay сразу при инициализации
        clearOverlay(emblaApi);

        const onScroll = (api: EmblaApi) => applyOverlay(api, bgColors);
        const onSettle = (api: EmblaApi) => clearOverlay(api);

        emblaApi.on('scroll', onScroll);
        emblaApi.on('settle', onSettle);
        emblaApi.on('reInit', onSettle);

        return () => {
            emblaApi.off('scroll', onScroll);
            emblaApi.off('settle', onSettle);
            emblaApi.off('reInit', onSettle);
        };
    }, [emblaApi, bgColors]);
};

/** Единый хук для работы с embla-каруселью */
export const useEmbla = (bgColors: string[]) => {
    const [emblaRef, emblaApi] = useEmblaCarousel(
        {
            loop: true, // Зацикливание
            align: 'start', // Выравнивание слева
        },
        [
            Autoplay({
                delay: 5000, // Автоперелистывание через 5 секунд
                stopOnInteraction: false, // НЕ останавливаем а/п после действий пользователя
                stopOnFocusIn: false, // Останавливаем а/п при фокусировке
                stopOnMouseEnter: true, // Останавливаем а/п при наведении мыши
            }),
        ],
    );

    const { count, activeIndex } = useEmblaDots(emblaApi);

    useEmblaGradient(emblaApi, bgColors);

    const handlePrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
    const handleNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

    return { emblaRef, count, activeIndex, handlePrev, handleNext };
};
